import { z } from 'zod';
import { isoDateSchema } from './day';
import { languageSchema } from './role';
import { stayTotalCents } from './pricing';
import { paymentModeSchema, type PaymentMode } from './reservation';

// A checkout in flight holds its spots this long; a refusal releases at once. §13
export const CHECKOUT_TTL_MINUTES = 5;

// One visitor, two checkouts: a third releases their oldest. §13
export const MAX_OPEN_CHECKOUTS_PER_EMAIL = 2;

// What one online booking may take: a bot books half a beach, a family does not.
export const VISITOR_SPOT_LIMIT = 10;

// A visitor cannot check themselves in, so a deadline is never null here. §12
export const checkoutRequestSchema = z
  .object({
    spotIds: z.array(z.uuid()).min(1).max(VISITOR_SPOT_LIMIT),
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    holdMinutes: z
      .number()
      .int()
      .min(0)
      .max(24 * 60),
    guestName: z.string().trim().min(1).max(120),
    guestEmail: z.email().max(160),
    guestPhone: z.string().trim().max(40).nullable().default(null),
    language: languageSchema,
  })
  .refine((body) => body.endsOn >= body.startsOn, {
    path: ['endsOn'],
    message: 'must not end before it starts',
  });

export type CheckoutRequest = z.infer<typeof checkoutRequestSchema>;

// taken is the late payment whose spots had gone: refunded, and said so. §13
export const CHECKOUT_STATES = [
  'pending',
  'confirmed',
  'failed',
  'cancelled',
  'expired',
  'taken',
] as const;

export const checkoutStateSchema = z.enum(CHECKOUT_STATES);

export type CheckoutState = z.infer<typeof checkoutStateSchema>;

// redirectUrl is null where nothing is owed online: the booking is already made.
export const checkoutStartedSchema = z.object({
  code: z.string(),
  state: checkoutStateSchema,
  amountCents: z.number().int().nonnegative(),
  currency: z.string(),
  paymentMode: paymentModeSchema,
  redirectUrl: z.url().nullable(),
});

export type CheckoutStarted = z.infer<typeof checkoutStartedSchema>;

// What the venue charges online per its mode; nothing owed is nothing taken. §13
export function checkoutAmountCents(
  mode: PaymentMode,
  dayPricesCents: readonly number[],
  days: number,
  feeCents: number,
): number {
  if (mode === 'none') return 0;
  if (mode === 'hold_fee') return feeCents;
  return stayTotalCents(dayPricesCents, days, feeCents);
}

// A booking with nothing to pay is confirmed where it stands. §13
export function needsProvider(amountCents: number): boolean {
  return amountCents > 0;
}

// What is left of a checkout's hold, never below nothing. §13
export function heldSecondsLeft(untilMs: number, nowMs: number): number {
  return Math.max(0, Math.ceil((untilMs - nowMs) / 1000));
}

export function countdownOf(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
